import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared-kernel/primary/shared/shadcn/ui/select";
import { Combobox } from "@shared-kernel/primary/shared/combobox/combobox";
import { useState, useMemo, useEffect } from "react";
import { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { selectImpersonationCandidatesRetrieval } from "@user-management-context/read/application/use-cases/admin/impersonation-candidates-retrieval/selectors/impersonation-candidates-selectors";
import { retrieveImpersonationCandidates } from "@user-management-context/read/application/use-cases/admin/impersonation-candidates-retrieval/retrieve-impersonation-candidates";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@components/ui/alert-dialog";
import { ROLES_MAP } from "@user-management-context/shared/domain/types/enums/roles";

interface Props {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onImpersonate: (userId: string, activeRole: ROLES) => void;
}

const IMPERSONATABLE_ROLES = [ROLES.STUDENT, ROLES.TEACHER, ROLES.PROVIDER] as const;
type ImpersonatableRole = (typeof IMPERSONATABLE_ROLES)[number];

const roleLabels: Record<ImpersonatableRole, string> = {
  [ROLES.STUDENT]: ROLES_MAP[ROLES.STUDENT],
  [ROLES.TEACHER]: ROLES_MAP[ROLES.TEACHER],
  [ROLES.PROVIDER]: ROLES_MAP[ROLES.PROVIDER],
};

export function ImpersonateUserDialog({ isOpen, onOpenChange, onImpersonate }: Props) {
  const dispatch = useAppDispatch();
  const [selectedRole, setSelectedRole] = useState<ROLES | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { data: candidates } = useAppSelector(selectImpersonationCandidatesRetrieval);

  // Candidates are fetched per role from the backend (already filtered + role-scoped displayName).
  useEffect(() => {
    if (isOpen && selectedRole) dispatch(retrieveImpersonationCandidates(selectedRole));
  }, [isOpen, selectedRole, dispatch]);

  const userOptions = useMemo(
    () =>
      candidates.map(candidate => ({
        label: candidate.displayName,
        value: candidate.id,
      })),
    [candidates],
  );

  const handleClose = () => {
    onOpenChange(false);
    setSelectedRole(null);
    setSelectedUserId(null);
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent className="overflow-visible sm:max-w-[625px]">
        <AlertDialogHeader>
          <AlertDialogTitle>Changer d'utilisateur</AlertDialogTitle>
          <AlertDialogDescription />
        </AlertDialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <label htmlFor="role" className="text-sm font-medium">
              Sélectionner un rôle
            </label>
            <Select
              value={selectedRole || ""}
              onValueChange={value => {
                setSelectedRole(value as ROLES);
                setSelectedUserId(null);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choisir un rôle" />
              </SelectTrigger>
              <SelectContent>
                {IMPERSONATABLE_ROLES.map(role => (
                  <SelectItem
                    key={role}
                    value={role}
                    className="cursor-pointer hover:bg-accent hover:text-accent-foreground data-[state=checked]:bg-accent data-[state=checked]:text-accent-foreground"
                  >
                    {roleLabels[role]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {selectedRole && (
            <div className="grid gap-2">
              <label htmlFor="user" className="text-sm font-medium">
                Sélectionner un utilisateur
              </label>
              <Combobox
                options={userOptions}
                value={selectedUserId || ""}
                onChange={value => setSelectedUserId(value)}
                placeholder="Choisir un utilisateur"
                search={{
                  commandInputPlaceHolder: "Rechercher un utilisateur",
                  notFoundText: "Aucun utilisateur trouvé",
                }}
              />
            </div>
          )}
        </div>
        <AlertDialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Annuler
          </Button>
          <Button
            onClick={() => {
              if (selectedUserId && selectedRole) {
                setIsLoading(true);
                onImpersonate(selectedUserId, selectedRole);
              }
            }}
            disabled={isLoading || !selectedUserId || !selectedRole}
          >
            Valider
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
